import debounce from 'lodash.debounce';
import {filter, firstValueFrom, map, of, Subject, timeout} from 'rxjs';
import {v4 as uuid} from 'uuid';

import {
  ClientErrorResponse,
  ClientResponse,
  Conversation,
  CONVERSATION_STORAGE_PREFIX,
  ConversationContext,
  ConversationParticipant,
  initObservability,
  Observability,
  REQUEST_ACTION_DEFAULT_TIMEOUT_MS,
  RequestAction,
  TriggerParams,
} from '@stripe-internal/conversations-client';
import {createStore} from '@stripe-internal/state-manager';
import {
  InlineDataEventBus,
  InlineDataService,
} from '@stripe-internal/inline-data';
import {
  MessageFromParent,
  MessageFromParentType,
  MessageToParentType,
  Visibility,
} from '@stripe-internal/support-conversations-controller';
import {TwilioClient} from '@stripe-internal/twilio-conversations-client';
import {UniversalHelpClient} from '@stripe-internal/universal-help-conversations-client';

import {Api} from './lib/api/api';
import {
  App,
  AppBusMessage,
  AppCloseHandler,
  AppConfig,
  AppProfile,
  AppState,
  AppStateStore,
} from './types';
import {ClientFetch} from './lib/util/clientFetch';
import {createApi} from './lib/api/createApi';
import {createClient} from './lib/createClient';
import {createParentMessageBus} from './lib/parentMessageBus/createParentMessageBus';
import {devLog} from './lib/util/dev';
import {getStoredConversation} from './lib/getStoredConversation';
import {isStartFreshTriggerPoint} from './lib/util/triggerPoints';
import {newInlineDataService} from './lib/inlineDataService';
import {ParentMessageBus} from './lib/parentMessageBus/ParentMessageBus';
import {requestVisibilityChange} from './lib/parentMessageBus/messages';
import {SupportConversationsClient} from './lib/SupportConversationsClient';
import {SupportTriggerPointTriggerPoint} from '../generated/types';
import {wrapFetch} from './lib/wrapFetch';

export class AppImpl implements App {
  private _stateStore: AppStateStore;

  private _api: Api;

  private _client: SupportConversationsClient;

  private _chatClient: TwilioClient;

  private _uhClient: UniversalHelpClient;

  private _inlineDataEventBus = new InlineDataEventBus();

  private _inlineDataService: InlineDataService;

  private _parentMessageBus: ParentMessageBus;

  private _parentMessageSubject = new Subject<AppBusMessage>();

  private _observability: Observability;

  private _previousTriggerPoint?: string;

  private _conversationMap = new Map<string, Conversation>();

  // guards idempotent mount/unmount
  private _isActive = false;

  // guards only attempting resume on mount logic once
  private _hasResumed = false;

  constructor(
    config: AppConfig,
    profile: AppProfile,
    clientFetch: ClientFetch,
    parentMessageBus?: ParentMessageBus,
    onClose?: AppCloseHandler,
    observability: Partial<Observability> = {},
  ) {
    const {triggerPoint} = config;

    this._stateStore = createStore<AppState>({
      initialized: false,
      currentConversationId: null,
      showCloseConfirmation: false,
      config,
      profile,
      triggerParams: triggerPoint ? {triggerPoint} : undefined,
      visibility: (config.initialVisibility as Visibility) ?? 'open',
      onClose,
    });

    const getConfig = () => this.config;
    const getProfile = () => this.profile;
    const wrappedFetch = wrapFetch(clientFetch, getConfig, getProfile);
    this._api = createApi(wrappedFetch, getProfile);

    this._inlineDataService = newInlineDataService(wrappedFetch, {
      audience: this.config.audience,
      entry_point: this.config.entryPoint,
    });

    this._parentMessageBus =
      parentMessageBus ??
      createParentMessageBus({
        audience: config.audience,
        entryPoint: config.entryPoint,
        parentOrigin: config.parentOrigin ?? window.origin,
      });

    this._observability = initObservability(observability);

    const getApi = () => this.api;
    const requestAction = (action: RequestAction) => this.requestAction(action);
    const getObservability = () => this._observability;

    const {client, chatClient, uhClient} = createClient({
      getConfig,
      getProfile,
      getApi,
      getObservability,
      inlineDataEventBus: this.inlineDataEventBus,
      requestAction,
    });
    this._client = client;
    this._chatClient = chatClient;
    this._uhClient = uhClient;

    this.stateStore.setValue((state) => {
      state.initialized = true;
    });

    this.handleParentMessage = this.handleParentMessage.bind(this);

    this._onStorageEvent = this._onStorageEvent.bind(this);
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.minimize = this.minimize.bind(this);
  }

  // called on mount
  init() {
    if (this._isActive) {
      return;
    }

    this._isActive = true;
    this.subscribeToParentMessageBus();
    window.addEventListener('storage', this._onStorageEvent);

    devLog('App initialized with config:', this.config);
    this.parentMessageBus.post({
      type: MessageToParentType.SetWidgetInitialized,
    });

    // only ever check for resuming conversations on initial mount
    if (!this._hasResumed) {
      this._hasResumed = true;
      this.resumeExistingInProgressConversation();
    }
  }

  // called on unmount
  destroy() {
    if (!this._isActive) {
      return;
    }

    this._isActive = false;
    window.removeEventListener('storage', this._onStorageEvent);
    this._checkConversationState.cancel();
    this.unsubscribeFromParentMessageBus();
  }

  get initialized() {
    return this.state.initialized;
  }

  get stateStore() {
    return this._stateStore;
  }

  get state() {
    return this.stateStore.getState();
  }

  get config() {
    return this.state.config;
  }

  get profile() {
    return this.state.profile;
  }

  hasFlag(name: string) {
    return !!this.state.profile.featureFlags[name];
  }

  get api() {
    return this._api;
  }

  get client() {
    return this._client;
  }

  // PLEASE DO NOT USE DIRECTLY. This is only exposed to bind to STP
  get chatClient() {
    return this._chatClient;
  }

  get inlineDataEventBus() {
    return this._inlineDataEventBus;
  }

  get inlineDataService() {
    return this._inlineDataService;
  }

  get parentMessageBus() {
    return this._parentMessageBus;
  }

  updateConfig(config: AppConfig) {
    this.stateStore.setValue((state) => {
      state.config = config;
    });
  }

  updateProfile(profile: AppProfile) {
    this.stateStore.setValue((state) => {
      state.profile = profile;
    });
  }

  updateOnClose(onClose?: AppCloseHandler) {
    this.stateStore.setValue((state) => {
      state.onClose = onClose;
    });
  }

  updateParentMessageBus(parentMessageBus?: ParentMessageBus) {
    const nextParentMessageBus =
      parentMessageBus ??
      createParentMessageBus({
        audience: this.config.audience,
        entryPoint: this.config.entryPoint,
        parentOrigin: this.config.parentOrigin ?? window.origin,
      });

    if (this._parentMessageBus === nextParentMessageBus) {
      return;
    }

    this.unsubscribeFromParentMessageBus();
    this._parentMessageBus = nextParentMessageBus;
    if (this._isActive) {
      this.subscribeToParentMessageBus();
    }
  }

  subscribeToParentMessageBus() {
    this.parentMessageBus.addListener(this.handleParentMessage);
  }

  unsubscribeFromParentMessageBus() {
    this.parentMessageBus.removeListener(this.handleParentMessage);
  }

  // maybe this should just be a part of parentMessageBus
  requestAction(action: RequestAction) {
    // the nonce will associate a response with our request
    const nonce = uuid();

    const {waitForResponse, responseTimeout, action: requestedAction} = action;

    const parentMessagesObs = this._parentMessageSubject.asObservable();

    let response: Promise<ClientResponse>;

    if (waitForResponse) {
      const timeoutResponse = {
        code: 400,
        error: `Timeout waiting for request-action response for ${action.action.name}`,
      };

      response = firstValueFrom(
        parentMessagesObs.pipe(
          // find the response message that matches our request nonce
          filter((m) => m.nonce === nonce),
          // map the response data a successful ClientResponse
          map((m) => {
            return {code: 200, data: m.data};
          }),
          // handle any timeouts
          timeout({
            // use a specified or default timeout
            each: responseTimeout ?? REQUEST_ACTION_DEFAULT_TIMEOUT_MS,
            with: () => {
              // report timeouts
              // this.client.reportError(timeoutResponse.error);
              // return our defined timeout ClientResponse
              return of(timeoutResponse);
            },
          }),
        ),
      );
    } else {
      // if not waiting for a response, resolve immediately
      response = Promise.resolve({code: 200});
    }

    this.parentMessageBus.post({
      type: MessageToParentType.RequestAction,
      data: requestedAction,
      nonce,
    });

    return response;
  }

  get errors() {
    return this._observability.errors ?? {};
  }

  get metrics() {
    return this._observability.metrics ?? {};
  }

  get analytics() {
    return this._observability.analytics ?? {};
  }

  get visibility() {
    return this.state.visibility ?? 'open';
  }

  private getCurrentConversation(): Conversation | null {
    if (!this.state.currentConversationId) {
      return null;
    }

    return this.getConversation(this.state.currentConversationId) ?? null;
  }

  getConversation(conversationId: string): Conversation | undefined {
    return this._conversationMap.get(conversationId);
  }

  async startConversation({
    conversationId,
    initialQuery,
    restart = false,
    context,
  }: {
    conversationId?: string;
    initialQuery?: string;
    restart?: boolean;
    context?: Partial<ConversationContext>;
  } = {}): Promise<Conversation | null> {
    const {currentConversationId} = this.state;
    const currentTriggerPoint = this.currentTriggerPoint();

    const currentConversation = currentConversationId
      ? this.getConversation(currentConversationId)
      : undefined;

    try {
      const participant: ConversationParticipant = {
        id: this.profile.visitor.id,
        role: 'user',
        displayName: this.profile.visitor.displayName || 'User',
      };

      let triggerParams: TriggerParams = {
        triggerPoint: currentTriggerPoint,
      };

      if (initialQuery) {
        triggerParams.initialQuery = initialQuery;
      }

      if (restart) {
        triggerParams = {
          triggerPoint:
            (this._previousTriggerPoint as SupportTriggerPointTriggerPoint) ||
            currentTriggerPoint,
          initialQuery,
        };
      }

      const contextParams: ConversationContext = {
        userId: participant.id,
        audience: this.config.audience,
        entryPoint: this.config.entryPoint,
        triggerParams,
        ...context,
      };

      if (restart && currentConversation) {
        contextParams.tabs = currentConversation.tabs;
      }

      let conversationIdToAttachTo = restart ? undefined : conversationId;

      if (restart || isStartFreshTriggerPoint(triggerParams?.triggerPoint)) {
        const placeholderResp = await this._client.startConversation({
          participant,
          context: {
            ...contextParams,
            placeholderConversation: true,
          },
        });

        if (!('error' in placeholderResp)) {
          const placeholderConversation = placeholderResp.data.conversation;
          this._conversationMap.set(
            placeholderConversation.id,
            placeholderConversation,
          );
          this.stateStore.setValue((state) => {
            state.currentConversationId = placeholderConversation.id;
            state.error = null;
          });
        }

        if (currentConversation) {
          await currentConversation.leave();
        } else {
          const prevConversationResp = await this._client.inProgress({
            context: {
              ...contextParams,
              channel: 'bot',
            },
          });
          if (!('error' in prevConversationResp)) {
            if (
              prevConversationResp.data.inProgress &&
              prevConversationResp.data.conversationId
            ) {
              const {conversationId: prevConversationId} =
                prevConversationResp.data;
              await this._client.leaveConversation({
                conversationId: prevConversationId,
                participant,
              });

              if (conversationIdToAttachTo === prevConversationId) {
                conversationIdToAttachTo = undefined;
              }
            }
          }
        }
      }

      const startContextParams = restart
        ? {}
        : {
            ...contextParams,
          };

      const resp = await this._client.startConversation({
        conversationId: conversationIdToAttachTo,
        participant,
        context: {
          userId: participant.id,
          audience: this.config.audience,
          entryPoint: this.config.entryPoint,
          triggerParams,
          ...startContextParams,
        },
      });

      if ('error' in resp) {
        if (conversationIdToAttachTo) {
          this._client.leaveConversation({
            conversationId: conversationIdToAttachTo,
            participant,
          });
        }

        const errorResponse = resp as ClientErrorResponse;
        this.stateStore.setValue((state) => {
          state.error = {
            message: errorResponse.error,
            errorId: errorResponse.sentryEventId,
          };
        });

        // TODO: need to make sure this prevents resumeOrStart continuing to
        //  try to start a conv or otherwise allow it to pass through without
        //  setting an error (and await the end)
        return null;
      }

      const newConversation = resp.data.conversation;
      this._conversationMap.set(newConversation.id, newConversation);
      this.stateStore.setValue((state) => {
        state.currentConversationId = newConversation.id;
        state.error = null;
      });

      this._previousTriggerPoint =
        newConversation.context.triggerParams?.triggerPoint;

      this.parentMessageBus.post({
        type: MessageToParentType.ConversationLoaded,
        data: {
          conversationId: newConversation.id,
          triggerPoint: currentTriggerPoint,
          audience: this.config.audience,
          entryPoint: this.config.entryPoint,
        },
      });

      return newConversation;
    } catch (e) {
      this.stateStore.setValue((state) => {
        state.error = {
          message: (e as Error).message,
        };
      });
      return null;
    }
  }

  async restartConversation({
    initialQuery,
  }: {initialQuery?: string} = {}): Promise<Conversation | null> {
    return this.startConversation({
      initialQuery,
      restart: true,
    });
  }

  async endConversation(conversation?: Conversation): Promise<void> {
    if (conversation) {
      return this._endConversation(conversation);
    }

    return this._endCurrentConversation();
  }

  private async _endCurrentConversation() {
    const conversation = this.getCurrentConversation();
    if (conversation) {
      return this._endConversation(conversation);
    }
  }

  private async _endConversation(conversation: Conversation): Promise<void> {
    await conversation.leave();
    this._conversationMap.delete(conversation.id);

    if (this.state.currentConversationId === conversation.id) {
      this.stateStore.setValue((state) => {
        state.currentConversationId = null;
      });
    }
  }

  open() {
    this.stateStore.setValue((state) => {
      state.visibility = Visibility.Open;
      state.error = null;
    });

    requestVisibilityChange(this.parentMessageBus, Visibility.Open);
  }

  close(skipOnClose = false) {
    const conversation = this.getCurrentConversation();

    if (this.state.onClose && !skipOnClose) {
      const handled = this.state.onClose({
        close: (value) => this.close(value),
        conversation,
      });
      if (handled) {
        return;
      }
    }

    if (conversation) {
      const {channel, intent} = conversation.context;

      if (conversation.isActive) {
        if (intent === 'contact_support' && channel === 'request_a_call') {
          this.minimize();
          return;
        }

        if (
          !this.state.showCloseConfirmation &&
          intent === 'contact_support' &&
          channel === 'chat'
        ) {
          this.stateStore.setValue((state) => {
            state.showCloseConfirmation = true;
          });
          return;
        }
      }

      if (channel !== 'bot' || this.config.closeEndsBotConversation) {
        this._endConversation(conversation);
      }
    }

    this.stateStore.setValue((state) => {
      state.visibility = Visibility.Closed;
      state.currentConversationId = null;
      state.showCloseConfirmation = false;
      state.error = null;
    });

    requestVisibilityChange(this.parentMessageBus, Visibility.Closed);
  }

  minimize() {
    this.stateStore.setValue((state) => {
      state.visibility = Visibility.Minimized;
    });

    requestVisibilityChange(this.parentMessageBus, Visibility.Minimized);
  }

  private async fetchExistingInProgressConversationData() {
    const {audience, entryPoint} = this.config;
    const triggerPoint = this.currentTriggerPoint();
    const stored = getStoredConversation();

    const inProgressResp = await this.client.inProgressV2({
      // when we have an in-progress chat, we want to check if that specific chat
      // is in progress, otherwise we want to check if anything is in-progress,
      // which is why we otherwise leave it undefined
      conversationId:
        stored?.data?.context?.channel === 'chat' ? stored.id : undefined,
      context: stored?.data?.context ?? {
        audience,
        entryPoint,
        triggerParams: {triggerPoint},
      },
    });

    if ('error' in inProgressResp) {
      return;
    }

    if (inProgressResp.data.inProgress) {
      let conversationId: string | undefined = stored?.id;
      if (inProgressResp.data.conversationId) {
        conversationId = inProgressResp.data.conversationId;
      }

      let context: ConversationContext | undefined;
      if (stored && conversationId === stored.id) {
        context = {...stored.data?.context};
      }

      return {
        response: inProgressResp,
        conversationId,
        context,
      };
    } else if (stored) {
      Conversation.clearStorage({
        prefix: Conversation.storageKey(stored.id),
        all: true,
      });
    }

    return null;
  }

  async resumeOrStartConversation() {
    const existingInProgressData =
      await this.fetchExistingInProgressConversationData();
    if (existingInProgressData) {
      const {conversationId, context} = existingInProgressData;
      const conversation = await this.startConversation({
        conversationId,
        context,
      });

      if (conversation) {
        return conversation;
      }
    }

    return this.startConversation({
      initialQuery: this.currentTriggerParams().initialQuery,
    });
  }

  private async resumeExistingInProgressConversation() {
    const existingInProgressData =
      await this.fetchExistingInProgressConversationData();
    if (!existingInProgressData) {
      // Conversation.clearStorage({all: true});
      return;
    }

    const {response} = existingInProgressData;
    const {channel} = response.data;
    if (!channel || !['chat', 'request_a_call'].includes(channel)) {
      return;
    }

    this.open();
  }

  private currentTriggerParams(): TriggerParams {
    return (
      this.state.triggerParams ?? {
        triggerPoint: this.currentTriggerPoint(),
      }
    );
  }

  private currentTriggerPoint(): string {
    return (
      this.state.triggerParams?.triggerPoint ??
      this.config.triggerPoint ??
      'unspecified'
    );
  }

  // Lightweight filter: ignore non-conversation storage events immediately,
  // then debounce the actual state check so noisy bursts of storage events
  // (e.g. during a restart) settle before we act.
  private _onStorageEvent(e: StorageEvent) {
    if (!e.key?.startsWith(CONVERSATION_STORAGE_PREFIX)) {
      return;
    }

    this._checkConversationState();
  }

  // Debounced: check whether our current conversation is still valid.
  // This intentionally does NOT use the StorageEvent argument — it reads
  // current localStorage state so that it's correct regardless of which
  // event in a burst ends up triggering the check.
  private _checkConversationState = debounce(() => {
    const currentConversation = this.getCurrentConversation();
    if (!currentConversation) {
      return;
    }

    // Find the active conversation for this tab's config. This filters by
    // audience, entryPoint, and userId so we only look at conversations
    // belonging to the same merchant account.
    const {audience, entryPoint} = this.config;
    const userId = this.profile.visitor.id;
    const stored = getStoredConversation({audience, entryPoint, userId});

    // If the stored conversation IS the current one, it's still active.
    if (stored?.id === currentConversation.id) {
      return;
    }

    // Current conversation is no longer active. Only clear state if a
    // replacement conversation already exists in storage — this prevents
    // the tab from auto-starting a duplicate conversation before the
    // restarting tab's new conversation is fully created on the server.
    if (!stored) {
      return;
    }

    currentConversation.disconnect?.();
    this._conversationMap.delete(currentConversation.id);
    this.stateStore.setValue((state) => {
      state.currentConversationId = null;
    });
  }, 1000);

  private handleParentMessage(messageFromParent: MessageFromParent) {
    if (
      messageFromParent.type === MessageFromParentType.VisibilityChange &&
      ['open', 'closed', 'minimized'].includes(
        messageFromParent.data?.visibility,
      )
    ) {
      this.stateStore.setValue((state) => {
        const prevVisibility = state.visibility;
        state.visibility = messageFromParent.data.visibility;
        // open callers will always supply trigger params, but
        // if we RequestVisibilityChange to open, the controller will
        // respond just with the visibility change and no trigger params
        // so we don't want to clear out the trigger params in that case
        if (messageFromParent.data.triggerParams) {
          state.triggerParams = messageFromParent.data.triggerParams;
        }
        if (
          messageFromParent.data.visibility === Visibility.Open &&
          prevVisibility !== Visibility.Open
        ) {
          state.error = null;
        }
      });
    }

    if (messageFromParent.type === MessageFromParentType.CheckInitialized) {
      if (this._isActive) {
        this.parentMessageBus.post({
          type: MessageToParentType.SetWidgetInitialized,
        });
      }
    }

    this._parentMessageSubject.next(messageFromParent);
  }
}
